// ~/components/business/header.tsx
import {
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  LaptopOutlined,
  CarOutlined,
} from "@ant-design/icons";
import { Dropdown, Button, Tag, Avatar } from "antd";
import "~/styles/components/header.less";
import { useNavigate } from "@remix-run/react";
import { useMemo, useCallback } from "react"; // 添加 useEffect 用于调试
import { wrapAuth } from "~/utils/perm";

interface HeaderProps {
  userInfos?: {
    userInfo?: any;
    userRoles?: any[];
    permissionCodes?: string[];
  };
  menuKey?: string;
}

export default function Header({ userInfos, menuKey }: HeaderProps) {
  const navigate = useNavigate();

  // 使用默认值，避免 undefined 错误
  const userInfo = userInfos?.userInfo || {};
  const userRoles = userInfos?.userRoles || [];
  const permissionCodes = userInfos?.permissions || userInfos?.permissionCodes || [];

  // 检查是否有管理端权限
  const hasAdminPermission = useMemo(() => {
    return permissionCodes.includes("admin:data:board:read");
  }, [permissionCodes]);

  // 如果没有用户信息，显示加载状态
  if (!userInfos || !userInfo?.userName) {
    return (
      <header className="header">
        <div
          className="logo"
          style={{
            height: "48px",
            margin: "12px 16px",
            background: "rgba(255, 255, 255, 0.2)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#1890ff",
            fontSize: "16px",
            fontWeight: "bold",
            gap: "8px",
          }}
        >
          <CarOutlined />
          <span
            style={{
              fontSize: "16px",
              fontWeight: "bold",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {menuKey === "client" ? "嗨车购" : "运营后台"}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <Avatar
            size={32}
            style={{ backgroundColor: "#f0f0f0" }}
            icon={<UserOutlined />}
          />
        </div>
      </header>
    );
  }

  // 使用 useCallback 包裹处理函数，避免每次渲染都重新创建
  const handleLogout = useCallback(async () => {
    window.requestAnimationFrame(() => {
      sessionStorage.clear();
      const form = document.createElement("form");
      form.method = "POST";
      form.action = "/logout";
      document.body.appendChild(form);
      form.submit();
    });
  }, []);

  const handleSwitchToAdmin = useCallback(() => {
    try {
      window.open("/admin/workbench/info", "_blank");
    } catch (error) {
      console.error("Navigation error:", error);
    }
  }, []);

  const handleSwitchToClient = useCallback(() => {
    window.open("/pages/workbench/pending", "_blank");
  }, []);

  const menuItems = useMemo(() => [{
    key: "profile",
    label: (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          padding: "10px",
        }}
      >
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            flexDirection: "column",
            gap: 10,
            paddingBottom: "10px",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <div
            style={{
              fontWeight: 500,
              fontSize: 16,
              display: "flex",
              alignItems: "left",
              gap: 8,
              minWidth: "260px",
            }}
          >
            <Avatar
              size={32}
              style={{ backgroundColor: "#1890ff" }}
              icon={<UserOutlined />}
            />{" "}
            {userInfo?.userName || "未登录"}
          </div>
          <div style={{ color: "#999", display: "flex", gap: 8 }}>
            <span>电话:</span>
            {userInfo?.phoneNumber || "-"}
          </div>
          <div style={{ color: "#999", display: "flex", gap: 8 }}>
            <span>角色:</span>
            <div style={{ display: "flex" }}>
              {userRoles?.map((role: any) => (
                <Tag key={role.id} color="blue">
                  {role.roleName}
                </Tag>
              ))}
            </div>
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ width: "120px" }}>
            {menuKey === "client" ? (
                wrapAuth(
                <Button
                  type="default"
                  block
                  icon={<SettingOutlined />}
                  onClick={handleSwitchToAdmin}
                >
                  进入管理端
                </Button>,
                "admin:data:board:read"
                )
            ) : (
              <Button
                type="default"
                block
                icon={<LaptopOutlined />}
                onClick={handleSwitchToClient}
              >
                返回客户端
              </Button>
            )}
          </div>
          <div style={{ textAlign: "left" }}>
            <Button
              type="primary"
              style={{ width: "80px" }}
              danger
              block
              icon={<LogoutOutlined />}
              onClick={handleLogout}
            >
              退出
            </Button>
          </div>
        </div>
      </div>
    ),
  }], [userInfo, userRoles, menuKey, hasAdminPermission, handleSwitchToAdmin, handleSwitchToClient, handleLogout]);

  return (
    <header className="header">
      <div
        className="logo"
        style={{
          height: "48px",
          margin: "12px 16px",
          background: "rgba(255, 255, 255, 0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#1890ff",
          fontSize: "16px",
          fontWeight: "bold",
          gap: "8px",
        }}
      >
        <CarOutlined />
        <span
          style={{
            fontSize: "16px",
            fontWeight: "bold",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {menuKey === "client" ? "嗨车购" : "运营后台"}
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
        {typeof window !== "undefined" && (
          <Dropdown
            menu={{ items: menuItems }}
            placement="bottomRight"
            trigger={["click"]}
            arrow
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                cursor: "pointer",
              }}
            >
              <span style={{ fontWeight: 300, fontSize: 14, color: "#666" }}>
                {userInfo?.userName}
              </span>
              <Avatar
                size={32}
                style={{ backgroundColor: "#1890ff" }}
                icon={<UserOutlined />}
              />
            </div>
          </Dropdown>
        )}
      </div>
    </header>
  );
}